/**
 * Types of the "Rules and countries" content: the rules for every country, the seven
 * country guides, the six steps and the "warning or blocked" text.
 *
 * The texts are typed data in the frontend (generated once by
 * `scripts/build-rules-content.mjs`, then maintained here). Nothing is fetched: the
 * live mode per company comes from `GET /rules`, for managers and admins only.
 */

/** The seven country guides. The id is the URL segment: `/rules/<id>`. */
export const GUIDE_IDS = [
  "denmark",
  "united-kingdom",
  "north-america",
  "india",
  "france",
  "germany",
  "spain-mexico-czechia",
] as const;
export type GuideId = (typeof GUIDE_IDS)[number];

/** Active: I2Q does it today. Planned: described, not built yet. */
export type RuleStatus = "active" | "planned";

/** What sales sees on the case: held (blocked), a warning, or a note. */
export type SeenTone = "blocked" | "warning" | "info";

/** The mode of a registry rule for one company (backend `rules/data/modes.yaml`). */
export type RegistryMode = "block" | "warn" | "info" | "off";

export const TOPICS = [
  "mail-and-case",
  "customer",
  "items",
  "price",
  "freight",
  "tax",
  "quotation",
] as const;
export type RuleTopic = (typeof TOPICS)[number];

/** One rule explained: a rule for every country, or one speciality of a country. */
export interface Explanation {
  /** Unique over all rules and specialities; the anchor on the page (`#<id>`). */
  id: string;
  title: string;
  status: RuleStatus;
  /** The strongest thing sales sees for it (the chip on the card). */
  tone: SeenTone;
  topic: RuleTopic;
  checks: string;
  why: string;
  youSee: string;
  whatToDo: string;
  example: string;
  /** For an active rule with a part that is not built yet. */
  plannedPart?: string;
  /** Rule ids of the backend registry this card explains (may be empty). */
  registryIds: readonly string[];
  /** The rules for every country this speciality belongs to (country cards only). */
  glossaryIds: readonly string[];
  /** Snapshot of the modes of `registryIds` when the text was written (checked by a test). */
  modes?: Readonly<Record<string, Readonly<Record<string, RegistryMode>>>>;
  /** A former id, so an old link still opens the card. */
  renamedFrom?: string;
  /** For IT, never shown: where the rule comes from. */
  source: string;
}

export interface CountryGuide {
  id: GuideId;
  /** The short code of the content file: dk, uk, na, in, fr, de, es-mx-cz. */
  code: string;
  /** The title of the guide and of its card. */
  title: string;
  /** For "Specialities of ...". */
  shortName: string;
  /** Entity codes a case carries (dk, uk, usl, in, fr, wgg, es, mx, cz). */
  entities: readonly string[];
  /** Area role codes of `GET /auth/me` (dk, uk, usl, in, fr, de, es, mx, cz). */
  areaRoles: readonly string[];
  /** True when I2Q prepares drafts for the guide's area (for one part at least). */
  live: boolean;
  /** One line on the country's card in the overview: what matters most there. */
  cardLine: string;
  /** Shown above the guide when part of it is not switched on yet. */
  notLiveNote?: string;
  /**
   * "What I2Q cannot do yet" in the overview: the known gaps of the area in plain words
   * (docs/current-issues.md, docs/gap-register.md). Kept by hand, honest and short.
   */
  notYet?: readonly string[];
  summary: string;
  /** One paragraph per mailbox group. */
  howInquiriesArrive: readonly string[];
  /** Ordered: the most important first. */
  specialities: readonly Explanation[];
}

export interface HowItWorks {
  title: string;
  summary: string;
  steps: readonly { title: string; text: string }[];
}

export interface WarningOrBlocked {
  title: string;
  text: readonly string[];
  example: string;
}
